import React from "react";
import { Users, Shuffle, FileText, CheckSquare, Globe, Mail, Database } from "lucide-react";
import { FaWhatsapp, FaFacebook } from "react-icons/fa";
import { IconBadge } from "./components/cards";

// The 4-step "Connect → Unify → Enrich → Activate" row at the top of the
// page, plus a small static graphic on the right (channel icons flowing
// into one profile) — purely decorative, no live data.
const STEPS = [
  {
    icon: <Users size={16} />,
    title: "1. Connect",
    description: "Link CRMs, apps, and platforms in minutes, no code required.",
  },
  {
    icon: <Shuffle size={16} />,
    title: "2. Unify",
    description: "Records are matched and merged into one profile per customer.",
  },
  {
    icon: <FileText size={16} />,
    title: "3. Enrich",
    description: "Profiles gain context: journey stage, value, lifecycle signals.",
  },
  {
    icon: <CheckSquare size={16} />,
    title: "4. Activate",
    description: "Push unified profiles to the channels that reach customers.",
  },
];

const FLOW_ICONS = [
  { icon: <Globe size={13} />, className: "bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400" },
  { icon: <FaWhatsapp size={13} />, className: "bg-green-50 dark:bg-green-500/15 text-green-600 dark:text-green-400" },
  { icon: <Mail size={13} />, className: "bg-blue-50 dark:bg-blue-500/15 text-blue-600 dark:text-blue-400" },
  { icon: <FaFacebook size={13} />, className: "bg-indigo-50 dark:bg-indigo-500/15 text-indigo-600 dark:text-indigo-400" },
  { icon: <Database size={13} />, className: "bg-amber-50 dark:bg-amber-500/15 text-amber-600 dark:text-amber-400" },
];

export const WelcomeBanner: React.FC = () => (
  <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5 sm:p-6 shadow-sm">
    <h1 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-gray-100">Welcome to DataHub</h1>
    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-2xl">
      Bring together CRM, app, broadcast, and other customer data into one connected profile for better targeting
      and faster activation.
    </p>

    <div className="mt-5 flex flex-col lg:flex-row lg:items-center gap-6">
      {/* Step list */}
      <div className="flex-1 grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6 min-w-0">
        {STEPS.map((step) => (
          <div key={step.title} className="min-w-0">
            <div className="flex items-center gap-1.5 text-sm font-semibold text-gray-900 dark:text-gray-100">
              {step.icon}
              {step.title}
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">{step.description}</p>
          </div>
        ))}
      </div>

      {/* Decorative flow graphic — channel icons converging into one profile */}
      <div className="hidden lg:flex items-center gap-2 shrink-0 px-4">
        <div className="flex flex-col gap-1.5">
          {FLOW_ICONS.map((f, i) => (
            <IconBadge key={i} icon={f.icon} className={`!w-7 !h-7 ${f.className}`} />
          ))}
        </div>
        <div className="w-8 h-px border-t border-dashed border-gray-300 dark:border-gray-700" />
        <span className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
          <Users size={18} />
        </span>
        <div className="w-8 h-px border-t border-dashed border-gray-300 dark:border-gray-700" />
        <div className="w-24 h-16 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 p-2 flex flex-col gap-1">
          <div className="w-6 h-6 rounded-full bg-indigo-200 dark:bg-indigo-500/30" />
          <div className="h-1.5 w-full rounded bg-gray-200 dark:bg-gray-700" />
          <div className="h-1.5 w-2/3 rounded bg-gray-200 dark:bg-gray-700" />
        </div>
      </div>
    </div>

    <a
      href="/track/connections/new"
      className="mt-5 px-4 py-2.5 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors shadow-sm"
    >
      Connect your data
    </a>
  </div>
);

export default WelcomeBanner;
